// Geocoding service using Google Maps API
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;

// Cache for geocoding results to avoid repeated API calls
const geocodeCache = new Map();

/**
 * Geocode a location string to get latitude and longitude
 * @param {string} location - Location string (city, "city, state", or address)
 * @returns {Promise<{lat: number, lng: number}>} Coordinates or null if not found
 */
export const geocodeLocation = async (location) => {
  if (!location || !GOOGLE_MAPS_API_KEY) {
    return null;
  }

  // Check cache first
  if (geocodeCache.has(location)) {
    return geocodeCache.get(location);
  }

  try {
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
        location
      )}&key=${GOOGLE_MAPS_API_KEY}`
    );

    if (!response.ok) {
      throw new Error(`Geocoding API error: ${response.statusText}`);
    }

    const data = await response.json();

    if (data.results && data.results.length > 0) {
      const result = data.results[0];
      const coords = {
        lat: result.geometry.location.lat,
        lng: result.geometry.location.lng
      };
      // Cache the result
      geocodeCache.set(location, coords);
      return coords;
    }

    // Cache null result too, to avoid repeated failed requests
    geocodeCache.set(location, null);
    return null;
  } catch (error) {
    console.error('Geocoding error:', error);
    return null;
  }
};

/**
 * Geocode multiple locations in parallel
 * @param {Array<string>} locations - Array of location strings
 * @returns {Promise<Array<{location: string, coords: {lat, lng} | null}>>}
 */
export const geocodeMultiple = async (locations) => {
  const results = await Promise.all(
    locations.map(async (location) => ({
      location,
      coords: await geocodeLocation(location)
    }))
  );
  return results;
};

/**
 * Geocode providers data
 * @param {Array<{provider}>} providers - Array of provider objects with Location field
 * @returns {Promise<Array>} Providers with added lat/lng properties
 */
export const geocodeProviders = async (providers) => {
  const results = await Promise.allSettled(
    providers.map(async (provider) => {
      const location = provider["Location"] || provider.location || "";
      if (!location) {
        return provider; // Return as-is if no location
      }

      const coords = await geocodeLocation(location);
      if (coords) {
        return {
          ...provider,
          latitude: coords.lat,
          longitude: coords.lng,
          geocoded: true
        };
      }
      return provider; // Return without coords if geocoding fails
    })
  );

  // Filter out rejected promises and return successful geocoded providers
  return results
    .filter((result) => result.status === "fulfilled")
    .map((result) => result.value)
    .filter((provider) => provider.latitude && provider.longitude); // Only return geocoded providers
};

/**
 * Clear the geocoding cache
 */
export const clearGeocodeCache = () => {
  geocodeCache.clear();
};

/**
 * Get cache size
 */
export const getGeocacheSizeInfo = () => {
  return {
    size: geocodeCache.size,
    entries: Array.from(geocodeCache.keys())
  };
};
